import { useTranslation } from 'react-i18next';

import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import {
  WEEKDAYS,
  type AvailabilityWindow,
  type SalesAvailability,
  type SalesAvailabilityInput,
  type Weekday,
} from './passes';

/**
 * Sales-availability controls shared by the create and edit pass forms (PRD #164): two
 * weekday-and-date windows — `validDays` (when the pass applies) and `exclusionDays` (when it does
 * not, exclusions win). Weekdays are a set of ISO day names, persisted as a bitmask; leaving every
 * box unchecked means "all days". Dates are optional ISO (`yyyy-MM-dd`) bounds. When a window is
 * left fully open it is sent as unrestricted.
 */
export interface AvailabilityWindowDraft {
  weekdays: Weekday[];
  startDate: string;
  endDate: string;
}

export interface SalesAvailabilityDraft {
  validDays: AvailabilityWindowDraft;
  exclusionDays: AvailabilityWindowDraft;
  exclusionEnabled: boolean;
}

const emptyWindow: AvailabilityWindowDraft = { weekdays: [], startDate: '', endDate: '' };

export const emptySalesAvailability: SalesAvailabilityDraft = {
  validDays: emptyWindow,
  exclusionDays: emptyWindow,
  exclusionEnabled: false,
};

function windowDraftFrom(window: AvailabilityWindow): AvailabilityWindowDraft {
  return {
    weekdays: window.weekdays,
    startDate: window.startDate ?? '',
    endDate: window.endDate ?? '',
  };
}

/** Seed the form draft from a persisted pass (edit). Exclusion days start expanded only if any were set. */
export function salesAvailabilityDraftFrom(availability: SalesAvailability): SalesAvailabilityDraft {
  const exclusionDays = windowDraftFrom(availability.exclusionDays);
  return {
    validDays: windowDraftFrom(availability.validDays),
    exclusionDays,
    exclusionEnabled:
      exclusionDays.weekdays.length > 0 || exclusionDays.startDate !== '' || exclusionDays.endDate !== '',
  };
}

function windowInput(draft: AvailabilityWindowDraft) {
  return {
    weekdays: draft.weekdays,
    startDate: draft.startDate ? draft.startDate : undefined,
    endDate: draft.endDate ? draft.endDate : undefined,
  };
}

/** Map the draft back to the wire `SalesAvailabilityInput`; empty dates are omitted and hidden exclusions are cleared. */
export function salesAvailabilityInput(draft: SalesAvailabilityDraft): SalesAvailabilityInput {
  return {
    validDays: windowInput(draft.validDays),
    exclusionDays: draft.exclusionEnabled ? windowInput(draft.exclusionDays) : windowInput(emptyWindow),
  };
}

interface WindowFieldsProps {
  idPrefix: string;
  title?: string;
  draft: AvailabilityWindowDraft;
  onChange: (patch: Partial<AvailabilityWindowDraft>) => void;
}

function WindowFields({ idPrefix, title, draft, onChange }: WindowFieldsProps) {
  const { t } = useTranslation('common');

  function toggleWeekday(day: Weekday, checked: boolean) {
    const next = checked
      ? [...draft.weekdays, day]
      : draft.weekdays.filter((existing) => existing !== day);
    onChange({ weekdays: next });
  }

  return (
    <div className="grid gap-2">
      {title && <p className="text-sm font-medium">{title}</p>}
      <div className="flex flex-wrap gap-3">
        {WEEKDAYS.map((day) => (
          <label key={day} className="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              checked={draft.weekdays.includes(day)}
              onChange={(event) => toggleWeekday(day, event.target.checked)}
            />
            {t(`products.form.availability.weekday.${day}`)}
          </label>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">{t('products.form.availability.allDaysHint')}</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="grid gap-1">
          <Label htmlFor={`${idPrefix}-start`}>{t('products.form.availability.startDate')}</Label>
          <Input
            id={`${idPrefix}-start`}
            type="date"
            value={draft.startDate}
            max={draft.endDate || undefined}
            onChange={(event) => onChange({ startDate: event.target.value })}
          />
        </div>
        <div className="grid gap-1">
          <Label htmlFor={`${idPrefix}-end`}>{t('products.form.availability.endDate')}</Label>
          <Input
            id={`${idPrefix}-end`}
            type="date"
            value={draft.endDate}
            min={draft.startDate || undefined}
            onChange={(event) => onChange({ endDate: event.target.value })}
          />
        </div>
      </div>
    </div>
  );
}

interface SalesAvailabilityFieldsProps {
  draft: SalesAvailabilityDraft;
  onChange: (patch: Partial<SalesAvailabilityDraft>) => void;
}

export function SalesAvailabilityFields({ draft, onChange }: SalesAvailabilityFieldsProps) {
  const { t } = useTranslation('common');

  return (
    <fieldset className="grid gap-4 rounded-md border p-4">
      <legend className="px-1 text-sm font-medium">{t('products.form.availability.legend')}</legend>
      <WindowFields
        idPrefix="valid-days"
        title={t('products.form.availability.validDays')}
        draft={draft.validDays}
        onChange={(patch) => onChange({ validDays: { ...draft.validDays, ...patch } })}
      />
      <div className="grid gap-2">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            checked={draft.exclusionEnabled}
            onChange={(event) => onChange({ exclusionEnabled: event.target.checked })}
          />
          {t('products.form.availability.exclusionDays')}
        </label>
        {draft.exclusionEnabled && (
          <WindowFields
            idPrefix="exclusion-days"
            draft={draft.exclusionDays}
            onChange={(patch) => onChange({ exclusionDays: { ...draft.exclusionDays, ...patch } })}
          />
        )}
      </div>
    </fieldset>
  );
}
